import {
  Inject,
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  ViewChildren,
  AfterViewInit,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, of } from 'rxjs';
import { catchError, map, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import {
  ChannelUIds,
  MassNotificationTemplate,
  CommunicationTemplate,
  MassNotification,
  MassNotificationRecipient,
  TemplateViewControlType
} from 'src/app/modules/communications/store/communications/communications.model';
import * as fromComm from 'src/app/modules/communications/store/communications';
import { View, ViewComponent, ViewControlType, Workflow, WorkflowParentType, WorkflowStepAction } from 'src/app/modules/core/store/models/workflow.model';
import { DataApiService } from 'src/app/modules/management/store/data/data-api.service';
import { GridHelperService } from 'src/app/modules/core/services/grid-helper.service';
import {
  Dataset,
  Document,
  DocumentQueryParams,
  DocumentReader,
  FieldDataType,
  FilterGroup,
  QueryParams,
  RuleOperator,
  SchemaField,
  SystemFieldType,
  getDefaultSchemaFields
} from 'src/app/modules/core/store/models';
import { DataTableServerComponent } from '@app/modules/core/components/data-table-server/data-table.component';
import { GetRowIdParams, IServerSideDatasource, ValueGetterParams } from 'ag-grid-enterprise';
import { getCellRenderer, getValueGetter } from 'src/app/modules/core/components/ag-grid';
import { PicklistService } from '@app/modules/core/services/picklist/picklist.service';
import { v4 as uuidv4 } from 'uuid';
import { DatasetApiService } from '@app/modules/core/services/dataset-api.service';
import { cloneDeep } from 'lodash-es';
import { LogStatusUIds } from '../notification-log.model';

@Component({
    selector: 'topo-comms-log-status-detail',
    templateUrl: './status-detail.component.html',
    styleUrls: ['./status-detail.component.scss'],
    standalone: false
})
export class NotificationLogStatusDetailComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();

  _document: Document;
  @Input() set document(value: Document) {
    this._document = value;
    this.loadData(value);
  }
  get document(): Document {
    return this._document;
  }

  @Input() notification: MassNotification;
  @Input() title: string;
  @Output() statusClosed = new EventEmitter();

  filterGroup: FilterGroup;
  filterDocument: Document;
  dataset: Dataset;

  runTime: string;
  fromHour: number;
  toHour: number;
  severity: string;
  address: string;
  genericStatus: string;
  hasFilter: boolean;
  hasTime: boolean;
  hasAddress: boolean;
  hasNoInfo: boolean;
  hasGenericInfo: boolean;
  editView: View;
  documentControls = [];
  selectedViewContainer: ViewComponent;
  selectedViewControlContainerUId: string;
  viewControlContainers: ViewComponent[] = [];


  constructor(
    private translateService: TranslateService,
    private datasetApiService: DatasetApiService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadData(document: Document) {
    this.hasFilter = false;
    this.hasTime = false;
    this.hasAddress = false;
    this.hasNoInfo = false;
    if (!!!document) {
      this.hasNoInfo = true;
      this.changeDetector.markForCheck();
      return;
    }
    const statusUId = document['display_status_uid'];
    if (statusUId === LogStatusUIds.SubscriptionCheckFailed) {
      const datasetUId = document['dataset_uid'];
      if (!!datasetUId) {
        this.datasetApiService.getDataset(datasetUId, { includeWorkflow: true }).subscribe(ds => {
          this.dataset = ds;
          if (!!this.dataset) {
            this.setFilterData(document);
          } else {
            this.hasNoInfo = true;
          }
          this.changeDetector.markForCheck();
        });
      }
    } else if (statusUId === LogStatusUIds.DoNotDisturbInEffect) {
      this.setDnbData(document);
    } else if (statusUId === LogStatusUIds.AddressWasRemovedByPrefs) {
      this.setAddressData(document);
    } else if (!!document['status_text'])
    {
      this.hasGenericInfo = true;
      this.genericStatus = document['status_text'];
    } else
    {
      this.hasNoInfo = true;
    }
    this.changeDetector.markForCheck();
  }

  valueChanged($event: any): void {}

  private setAddressData(document: Document): void {
    const addressData = JSON.parse(document['context_data']);
    const severity = addressData['NotificationSeverity'];
    this.severity = this.translateService.instant('COMMUNICATIONS.NOTIFICATION-SEVERITY-' + severity);
    this.address = addressData['Address'];
    this.hasAddress = true;
  }

  private setDnbData(document: Document): void {
    const dndData = JSON.parse(document['context_data']);

    this.runTime = dndData['Hour'].toString();
    if (this.runTime.indexOf(':') == -1) {
      this.runTime += ':00';
    }
    this.fromHour = dndData['FromHour'];
    this.toHour = dndData['ToHour'];
    this.hasTime = true;
  }

  private setFilterData(document: Document): void {
    this.filterDocument = JSON.parse(document['document']);
    this.filterGroup = JSON.parse(document['context_data'])?.FailedFilter;
    const detailView = this.dataset?.workflow?.views?.find(v => v.name === 'detail');
    const filterFields = [];
    const editView = detailView?.controls?.length > 0 ? detailView?.controls[0] as View : null;

    this.fillFilterFields(this.filterGroup, filterFields);
    this.editView = cloneDeep(detailView);
    if (this.editView.controls.some(c => c.controlType === ViewControlType.ViewControlContainer)) {
      this.viewControlContainers = this.editView.controls;
      this.selectedViewContainer = this.viewControlContainers.length > 0 ? this.viewControlContainers[0] : null;
    }
    this.editView.controls[0].controls = [];
    // first, show fields included in filter
    editView.controls.forEach(c => {
      const field = this.dataset.schema.fields.find(f => f.uId === c.dataSetFieldUId);
      if (!!field) {
        if (filterFields.includes(field.systemName)) {
          this.editView.controls[0].controls.push(c);
        }
      }
    });
    // now, show rest of the fields
    editView.controls.forEach(c => {
      const field = this.dataset.schema.fields.find(f => f.uId === c.dataSetFieldUId);
      if (!!field) {
        if (!filterFields.includes(field.systemName)) {
          this.editView.controls[0].controls.push(c);
        }
      } else {
        this.editView.controls[0].controls.push(c);
      }
    });
    this.hasFilter = true;
  }

  private fillFilterFields(filterGroup: FilterGroup, fields: string[]): void {
    filterGroup.filters.forEach(f => {
      if (!fields.includes(f.field)) {
        fields.push(f.field);
      }
    });

    filterGroup.filterGroups.forEach(g => {
      this.fillFilterFields(g, fields);
    });
  }

  close(): void {
    this.statusClosed.emit();
  }
}
